import { useEffect, useMemo, useState } from 'react';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemTitle,
} from '@/components/ui/item';
import { cn } from '@/lib/utils';
import { ORGANIZATION } from 'components/IAP/ICSConstants';
import ActionDrawer from 'components/IAP/ActionDrawer';
import ResourceSetEditDrawer from 'components/IAP/ResourceSetEditDrawer';
import { LaunchScenarioButton } from 'components/Scenarios/LaunchScenarioButton';
import {
  useOrgActions,
  type GetOrgActionResponse,
} from 'components/IAP/hooks/orgActionHooks';
import {
  useResourceSets,
  type ResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';
import { type ResourceRow } from 'components/MapPage/CorporateViewTabs/ResourceTab/ResourceTabCard';
import { CfaHeleneAssetItem } from '../NotificationsTab/CfaHeleneAssetItem';
import { useHiddenAssetSymbols } from './useHiddenAssetSymbols';
import { TriggeringMapItems } from './EventTriggeringItems';
import {
  CFA_HELENE_STORES,
  CFA_HELENE_STORES_LAYER_ID,
} from 'components/MapPage/CorporateViewTabs/LayersTab/cfa-demo/historicalHurricanesHeleneData';
import { EPA_ERMA_NPL_LAYER_ID } from 'components/MapPage/CorporateViewTabs/LayersTab/cfa-demo/epaHeleneErmaLayers';
import {
  getCachedNplSitesInHeleneCone,
  queryNplSitesInHeleneCone,
} from 'components/MapPage/CorporateViewTabs/LayersTab/cfa-demo/queryNplSitesInHeleneCone';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { buildPointFeatureGraphic } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/focusFeatureOnMap';
import { applyHeleneResourcePopupToGraphic } from 'components/MapPage/CorporateViewTabs/LayersTab/cfa-demo/heleneResourcePopup';
import {
  useGetRostersForGroup,
  type GroupRosterItem,
} from 'hooks/incident/useRoster';
import {
  useOrgEvents,
  useUpdateOrgEvent,
  type OrgEvent,
  type OrgEventChecklistItemInstance,
} from 'hooks/orgEvents/orgEventHooks';
import {
  eventLevelChecklistItems,
  hasPersistedResourceChecklistItems,
  resourceChecklistItems,
} from 'hooks/orgEvents/orgEventChecklistItems';
import { useAors } from 'hooks/useAors';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import {
  useTropicalDemoKind,
  useUscgFocRequirementsFlag,
} from 'utils/featureFlags';
import {
  getMemphisRegionalThreats,
  heleneStoreIdFromResourceNotes,
  isHeleneConeResourceSet,
  isMemphisThreatOrgActionName,
  MEMPHIS_RESPONSE_CHECKLIST_ITEMS,
} from '../NotificationsTab/memphisThreatNotification';
import {
  EPA_GULF_FACILITIES,
  EPA_RESPONSE_CHECKLIST_ITEMS,
  epaFacilityIdFromResourceNotes,
  getEpaRegionalThreats,
  isEpaGulfOrgActionName,
  isEpaGulfResourceSet,
} from '../NotificationsTab/epaGulfThreatNotification';
import {
  findUscgFocSeDemoSite,
  getUscgFocSoutheastRegionalThreats,
  isUscgFocSeResourceSet,
  isUscgFocSeThreatOrgActionName,
  USCG_FOC_SE_ORG_EVENT_DEMO_KEY,
  USCG_FOC_SE_RESPONSE_CHECKLIST_ITEMS,
  uscgFocSeAssetIdFromResourceNotes,
} from '../NotificationsTab/uscgFocSoutheastThreatNotification';
import {
  assetsInCone,
  USCG_FOC_SE_ASSETS_LAYER_ID,
} from '../LayersTab/foc-demo/uscgFocSoutheastTropicalData';
import {
  facilityIdFromAnyFacilityDemoNotes,
  findAnyFacilityDemoSite,
  getFacilityDemoPack,
} from '../NotificationsTab/facilityDemoPack';
import {
  resolveResourceAutomationRuleChecklists,
  resourceHasAutomationRule,
  type AutomationRuleTypeRef,
} from './automationRuleChecklists';
import { useOrgEventAutomationRules } from 'hooks/orgEvents/orgEventAutomationHooks';
import { ChecklistNotifyComposeModal } from './ChecklistNotifyComposeModal';
import { COURSES_OF_ACTION_LABEL } from './eventExpandLabels';
import { formatAssignedActionPoc } from './formatAssignedActionPoc';

const EPA_ASSET_PAGE_SIZE = 5;

function findEpaGulfSite(
  siteId: string | undefined,
  cachedSites = getCachedNplSitesInHeleneCone()
) {
  if (!siteId) return undefined;
  return (
    cachedSites?.find((site) => site.id === siteId) ??
    EPA_GULF_FACILITIES.find((entry) => entry.id === siteId)
  );
}

function actionStatusBadgeClass(status: string | null | undefined): string {
  switch (status) {
    case 'Closed':
      return 'tw:border-transparent tw:bg-emerald-400/20 tw:text-emerald-800 tw:dark:text-emerald-200';
    case 'Current/Ongoing':
      return 'tw:border-transparent tw:bg-amber-400/25 tw:text-amber-800 tw:dark:text-amber-200';
    case 'Blocked':
      return 'tw:border-transparent tw:bg-destructive/15 tw:text-destructive';
    case 'Planned':
    default:
      return 'tw:border-transparent tw:bg-blue-500/20 tw:text-blue-100';
  }
}

function resourceSetToRow(
  resourceSet: ResourceSet,
  groupMembers: GroupRosterItem[],
  cachedNplSites?: ReturnType<typeof getCachedNplSitesInHeleneCone>,
  rules?: AutomationRuleTypeRef[]
): ResourceRow {
  const coordinates =
    resourceSet.geometry &&
    typeof resourceSet.geometry === 'object' &&
    Array.isArray(resourceSet.geometry.coordinates)
      ? resourceSet.geometry.coordinates
      : undefined;

  const storeId =
    heleneStoreIdFromResourceNotes(resourceSet.notes) ??
    epaFacilityIdFromResourceNotes(resourceSet.notes) ??
    facilityIdFromAnyFacilityDemoNotes(resourceSet.notes) ??
    uscgFocSeAssetIdFromResourceNotes(resourceSet.notes);
  const focAsset = storeId
    ? assetsInCone().find((entry) => entry.id === storeId)
    : undefined;
  const store = storeId
    ? (CFA_HELENE_STORES.find((entry) => entry.id === storeId) ??
      findEpaGulfSite(storeId, cachedNplSites) ??
      findAnyFacilityDemoSite(storeId))
    : undefined;

  const latitude =
    coordinates?.[1] != null
      ? Number(coordinates[1])
      : store != null
        ? store.lat
        : focAsset != null
          ? focAsset.lat
          : undefined;
  const longitude =
    coordinates?.[0] != null
      ? Number(coordinates[0])
      : store != null
        ? store.lon
        : focAsset != null
          ? focAsset.lon
          : undefined;

  const pocMember = resourceSet.point_of_contact
    ? groupMembers.find((m) => m.user_guid === resourceSet.point_of_contact)
    : undefined;
  const resolved = resolveResourceAutomationRuleChecklists(resourceSet, rules);

  return {
    id: resourceSet.id,
    resource_set_id: resourceSet.resource_set_id,
    name: resourceSet.name,
    kind: resourceSet.kind,
    type: resourceSet.type || '',
    assetStatus: resourceSet.asset_status || '',
    assetStatusTimestamp: resourceSet.asset_status_timestamp || '',
    incident: resourceSet.incident_details?.[0]
      ? {
          id: resourceSet.incident_details[0].id,
          name: resourceSet.incident_details[0].name,
        }
      : null,
    incident_details: resourceSet.incident_details,
    owning_organization: resourceSet.owning_organization || '',
    description: resourceSet.description,
    datetimeOrdered: resourceSet.date_time_ordered,
    datetimeOrderedOffset: resourceSet.datetime_ordered_offset,
    quantity: resourceSet.quantity,
    status: resourceSet.current_status || resourceSet.status,
    inRequest: resourceSet.in_request,
    aor: resourceSet.aor,
    opcon: resourceSet.opcon,
    tacon: resourceSet.tacon,
    persons_on_board: resourceSet.persons_on_board,
    hull_tail_number: resourceSet.hull_tail_number,
    unit: resourceSet.unit,
    address: resourceSet.address,
    symbology: resourceSet.symbology,
    latitude,
    longitude,
    notes: resourceSet.notes,
    threats: resolved.automationRules,
    threat_checklists: resolved.automationRuleChecklists,
    automation_rules: resolved.automationRules,
    automation_rule_checklists: resolved.automationRuleChecklists,
    eta: resourceSet.eta,
    etaTimezoneOffset: resourceSet.eta_timezone_offset,
    assignees: resourceSet.resource_in_incident_details
      ? resourceSet.resource_in_incident_details.flatMap((detail) =>
          (detail.assignees || []).map((assignee) => ({
            userGuid: assignee.id,
            userName: assignee.name,
            emailAddress: assignee.email,
          }))
        )
      : [],
    pointOfContact: pocMember
      ? {
          userGuid: pocMember.user_guid,
          emailAddress: pocMember.email_address,
          userName: pocMember.user_name,
        }
      : undefined,
  };
}

type CfaHeleneImpactSectionsProps = {
  /** Prefer these org-action IDs for the checklist when present. */
  responseChecklistActionIds?: string[] | null;
  /** Typed checklist instances (org actions + send notifications). */
  responseChecklistItems?: OrgEventChecklistItemInstance[] | null;
  /** When set, notify-item send can stamp sentNotificationId on the event. */
  orgEventId?: string | null;
  eventName?: string | null;
  /** When false, only the response checklist is rendered. */
  includeAssets?: boolean;
  assetItemDensity?: 'default' | 'comfortable';
  /** Live match set; wins over the fetched event row. */
  impactedResourceIds?: string[] | null;
  /** Preview / unsaved event fields when `orgEventId` is not in the list. */
  orgEventOverride?: OrgEvent | null;
};

/** Shared Assets at Risk + Courses of Action used by event expands and preview. */
export function CfaHeleneImpactSections({
  responseChecklistActionIds,
  responseChecklistItems,
  orgEventId,
  eventName,
  includeAssets = true,
  assetItemDensity = 'default',
  impactedResourceIds: impactedResourceIdsProp,
  orgEventOverride,
}: CfaHeleneImpactSectionsProps) {
  const demoKind = useTropicalDemoKind();
  const focEnabled = useUscgFocRequirementsFlag();
  const cfaEnabled = demoKind != null;
  const facilityPack = getFacilityDemoPack(demoKind);
  const { data: orgEvents = [] } = useOrgEvents();
  const focSeEvent = useMemo(() => {
    if (!focEnabled) return false;
    if (orgEventOverride?.demo_key === USCG_FOC_SE_ORG_EVENT_DEMO_KEY) {
      return true;
    }
    const matched = orgEvents.find((event) => event.id === orgEventId);
    return matched?.demo_key === USCG_FOC_SE_ORG_EVENT_DEMO_KEY;
  }, [focEnabled, orgEventOverride?.demo_key, orgEvents, orgEventId]);
  const regionalThreats = useMemo(
    () =>
      focSeEvent
        ? getUscgFocSoutheastRegionalThreats()
        : facilityPack
          ? facilityPack.getRegionalThreats()
          : demoKind === 'epa'
            ? getEpaRegionalThreats()
            : getMemphisRegionalThreats(),
    [demoKind, facilityPack, focSeEvent]
  );
  const { data: orgActions = [] } = useOrgActions(ORGANIZATION);
  const { data: resourceSets = [] } = useResourceSets();
  const { data: automationRules = [] } = useOrgEventAutomationRules();
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: groupMembers = [] } = useGetRostersForGroup(groupGuid || '');
  const { data: aors = [] } = useAors();
  const { focusFeatureOnMap, mapView } = useUscgMapContext();
  const updateOrgEvent = useUpdateOrgEvent();
  const persistableEventId =
    orgEventId && orgEventId !== 'preview' ? orgEventId : null;
  const orgEvent = useMemo(
    () =>
      orgEventOverride ??
      orgEvents.find((event) => event.id === orgEventId) ??
      null,
    [orgEventOverride, orgEvents, orgEventId]
  );

  const { hiddenIds: hiddenSymbolIds, setVisible: setAssetSymbolVisible } =
    useHiddenAssetSymbols();
  const [selectedResourceSetId, setSelectedResourceSetId] = useState<
    string | null
  >(null);
  const [openAssetIds, setOpenAssetIds] = useState<Set<string>>(
    () => new Set()
  );
  const [selectedAction, setSelectedAction] =
    useState<GetOrgActionResponse | null>(null);
  const [selectedResource, setSelectedResource] = useState<ResourceSet | null>(
    null
  );
  const [notifyItem, setNotifyItem] = useState<Extract<
    OrgEventChecklistItemInstance,
    { kind: 'send_notification' }
  > | null>(null);
  const [localChecklistItems, setLocalChecklistItems] = useState<
    OrgEventChecklistItemInstance[] | null
  >(null);
  const [nplSitesReady, setNplSitesReady] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  const [assetPage, setAssetPage] = useState(1);

  useEffect(() => {
    setLocalChecklistItems(null);
  }, [responseChecklistItems, orgEventId]);

  useEffect(() => {
    if (demoKind !== 'epa') return;
    let cancelled = false;
    void queryNplSitesInHeleneCone()
      .then(() => {
        if (!cancelled) setNplSitesReady(true);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [demoKind]);

  /**
   * The event's own checklist ids. There is no longer a name-matching fallback:
   * `materializeChecklistForEvent` writes these ids when a rule fires, so an
   * event with none genuinely has no checklist, and guessing from labels
   * silently attached whatever org action happened to share a name.
   */
  const checklistActions = useMemo(() => {
    const byId = new Map(
      orgActions
        .filter((action) => !action.archived)
        .map((action) => [String(action.action_id), action] as const)
    );
    return (responseChecklistActionIds ?? [])
      .filter(Boolean)
      .map((id) => byId.get(String(id)))
      .filter((action): action is GetOrgActionResponse => !!action);
  }, [orgActions, responseChecklistActionIds]);

  const persistedChecklistItems = useMemo(
    () => localChecklistItems ?? responseChecklistItems ?? [],
    [localChecklistItems, responseChecklistItems]
  );
  const eventHasPersistedResourceChecklists =
    hasPersistedResourceChecklistItems(persistedChecklistItems);

  const mixedChecklist = useMemo((): OrgEventChecklistItemInstance[] => {
    const items = persistedChecklistItems;
    if (items.length > 0) return eventLevelChecklistItems(items);

    if (checklistActions.length > 0) {
      return checklistActions.map(
        (action): OrgEventChecklistItemInstance => ({
          kind: 'org_action',
          actionId: String(action.action_id),
          label: action.name,
        })
      );
    }

    /**
     * Helene / Memphis / FOC demo: the threshold event is created without
     * checklist ids, while Response Checklist org actions are ensured
     * separately. Only those exact labels are used — no general name matching.
     */
    const isDemoAction = focSeEvent
      ? isUscgFocSeThreatOrgActionName
      : facilityPack
        ? facilityPack.isOrgActionName
        : demoKind === 'epa'
          ? isEpaGulfOrgActionName
          : isMemphisThreatOrgActionName;
    const byName = new Map(
      orgActions
        .filter((action) => !action.archived && isDemoAction(action.name))
        .map((action) => [action.name, action] as const)
    );

    if (focSeEvent) {
      return USCG_FOC_SE_RESPONSE_CHECKLIST_ITEMS.flatMap(
        (item): OrgEventChecklistItemInstance[] => {
          const action = byName.get(item.label);
          if (!action) return [];
          return [
            {
              kind: 'org_action',
              actionId: String(action.action_id),
              label: action.name,
            },
          ];
        }
      );
    }

    if (facilityPack || demoKind === 'epa') {
      const checklistItems = facilityPack
        ? ((orgEvent &&
            facilityPack.responseChecklistItemsForEvent?.(orgEvent)) ??
          facilityPack.responseChecklistItems)
        : EPA_RESPONSE_CHECKLIST_ITEMS;
      return checklistItems.flatMap((item): OrgEventChecklistItemInstance[] => {
        if (item.kind === 'send_notification') {
          return [
            {
              kind: 'send_notification',
              id: item.id,
              label: item.label,
              notify: item.notify,
            },
          ];
        }
        const action = byName.get(item.label);
        if (!action) return [];
        return [
          {
            kind: 'org_action',
            actionId: String(action.action_id),
            label: action.name,
          },
        ];
      });
    }

    return MEMPHIS_RESPONSE_CHECKLIST_ITEMS.flatMap(
      (item): OrgEventChecklistItemInstance[] => {
        const action = byName.get(item.label);
        if (!action) return [];
        return [
          {
            kind: 'org_action',
            actionId: String(action.action_id),
            label: action.name,
          },
        ];
      }
    );
  }, [
    persistedChecklistItems,
    checklistActions,
    orgActions,
    demoKind,
    facilityPack,
    focSeEvent,
    orgEvent,
  ]);

  const actionById = useMemo(() => {
    const map = new Map<string, GetOrgActionResponse>();
    for (const action of orgActions) {
      if (!action.archived) map.set(String(action.action_id), action);
    }
    return map;
  }, [orgActions]);

  const cachedNplSites = nplSitesReady ? getCachedNplSitesInHeleneCone() : null;

  const impactedResourceIds =
    impactedResourceIdsProp ?? orgEvent?.impacted_resource_ids;
  const coneResources = useMemo(() => {
    const useExactIds =
      Array.isArray(impactedResourceIds) &&
      (impactedResourceIds.length > 0 || orgEventId === 'preview');
    if (useExactIds) {
      if (impactedResourceIds.length === 0) return [];
      const byId = new Map(
        resourceSets.map((resource) => [resource.resource_set_id, resource])
      );
      return impactedResourceIds
        .map((id) => byId.get(id))
        .filter((resource): resource is ResourceSet => !!resource);
    }
    return resourceSets.filter((resource) => {
      if (focSeEvent) return isUscgFocSeResourceSet(resource);
      if (facilityPack) {
        if (!facilityPack.isResourceSet(resource)) return false;
        if (
          resourceHasAutomationRule(
            resource,
            facilityPack.primaryAutomationRuleName
          )
        ) {
          return true;
        }
        const category = orgEvent?.type;
        if (!category) return false;
        const hasRuleFields =
          (resource.automation_rules?.length ?? 0) > 0 ||
          Object.keys(resource.automation_rule_checklists ?? {}).length > 0;
        if (hasRuleFields) return false;
        return (
          Array.isArray(resource.threats) && resource.threats.includes(category)
        );
      }
      if (demoKind === 'epa') return isEpaGulfResourceSet(resource);
      return isHeleneConeResourceSet(resource);
    });
  }, [
    resourceSets,
    demoKind,
    orgEvent?.type,
    impactedResourceIds,
    orgEventId,
    facilityPack,
    focSeEvent,
  ]);

  const filteredConeResources = useMemo(() => {
    if (demoKind !== 'epa') return coneResources;
    const query = assetSearch.trim().toLowerCase();
    if (!query) return coneResources;
    return coneResources.filter((resource) => {
      const row = resourceSetToRow(
        resource,
        groupMembers,
        cachedNplSites,
        automationRules
      );
      const site = findEpaGulfSite(
        epaFacilityIdFromResourceNotes(resource.notes),
        cachedNplSites
      );
      const haystack = [
        resource.name,
        resource.description,
        resource.address,
        row.address,
        row.name,
        site?.city,
        site?.state,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [
    coneResources,
    demoKind,
    assetSearch,
    groupMembers,
    cachedNplSites,
    automationRules,
  ]);

  const assetPageCount = Math.max(
    1,
    Math.ceil(filteredConeResources.length / EPA_ASSET_PAGE_SIZE)
  );

  useEffect(() => {
    setAssetPage(1);
  }, [assetSearch]);

  useEffect(() => {
    if (assetPage > assetPageCount) setAssetPage(assetPageCount);
  }, [assetPage, assetPageCount]);

  const visibleConeResources =
    demoKind === 'epa'
      ? filteredConeResources.slice(
          (assetPage - 1) * EPA_ASSET_PAGE_SIZE,
          assetPage * EPA_ASSET_PAGE_SIZE
        )
      : coneResources;

  const focusHeleneStoreOnMap = (resource: ResourceSet) => {
    const storeId = focSeEvent
      ? uscgFocSeAssetIdFromResourceNotes(resource.notes)
      : facilityPack
        ? facilityPack.facilityIdFromResourceNotes(resource.notes)
        : demoKind === 'epa'
          ? epaFacilityIdFromResourceNotes(resource.notes)
          : heleneStoreIdFromResourceNotes(resource.notes);
    const store = focSeEvent
      ? (() => {
          const facility = findUscgFocSeDemoSite(storeId);
          return facility
            ? {
                id: facility.id,
                storeNumber: '',
                name: facility.name,
                city: facility.city,
                state: facility.state,
                lon: facility.lon,
                lat: facility.lat,
                inCone: facility.inCone,
              }
            : undefined;
        })()
      : facilityPack
        ? (() => {
            const facility = findAnyFacilityDemoSite(storeId);
            return facility
              ? {
                  id: facility.id,
                  storeNumber: '',
                  name: facility.name,
                  city: facility.city,
                  state: facility.state,
                  lon: facility.lon,
                  lat: facility.lat,
                  inCone: true,
                }
              : undefined;
          })()
        : demoKind === 'epa'
          ? (() => {
              const facility = findEpaGulfSite(storeId, cachedNplSites);
              return facility
                ? {
                    id: facility.id,
                    storeNumber: '',
                    name: facility.name,
                    city: facility.city,
                    state: facility.state,
                    lon: facility.lon,
                    lat: facility.lat,
                    inCone: true,
                  }
                : undefined;
            })()
          : storeId
            ? CFA_HELENE_STORES.find((entry) => entry.id === storeId)
            : undefined;
    const aorNames = {
      opcon: aors.find((a) => a.id === resource.opcon)?.name,
      tacon: aors.find((a) => a.id === resource.tacon)?.name,
    };

    const focusFromResourceGeometry = () => {
      const coordinates =
        resource.geometry &&
        typeof resource.geometry === 'object' &&
        Array.isArray(resource.geometry.coordinates)
          ? resource.geometry.coordinates
          : undefined;
      const longitude =
        coordinates?.[0] != null ? Number(coordinates[0]) : store?.lon;
      const latitude =
        coordinates?.[1] != null ? Number(coordinates[1]) : store?.lat;

      if (
        longitude == null ||
        latitude == null ||
        Number.isNaN(longitude) ||
        Number.isNaN(latitude)
      ) {
        return;
      }

      const fallback = buildPointFeatureGraphic({
        longitude,
        latitude,
        title: resource.name,
        attributes: {
          id: storeId,
          storeNumber: store?.storeNumber,
          name: resource.name,
        },
      });
      applyHeleneResourcePopupToGraphic({
        graphic: fallback,
        resource,
        store,
        aorNames,
      });
      void focusFeatureOnMap({ graphic: fallback });
    };

    if (demoKind === 'epa') {
      const nplLayer = mapView?.map?.findLayerById(EPA_ERMA_NPL_LAYER_ID);
      if (nplLayer instanceof FeatureLayer && storeId) {
        const escapedId = storeId.replace(/'/g, "''");
        void nplLayer
          .queryFeatures({
            where: `Site_EPA_ID = '${escapedId}'`,
            returnGeometry: true,
            outFields: ['*'],
            num: 1,
          })
          .then(({ features }) => {
            const hit = features[0];
            if (hit?.geometry) {
              if (nplLayer.popupTemplate) {
                hit.popupTemplate = nplLayer.popupTemplate;
              }
              void focusFeatureOnMap({ graphic: hit });
              return;
            }
            focusFromResourceGeometry();
          })
          .catch(() => {
            focusFromResourceGeometry();
          });
        return;
      }
      focusFromResourceGeometry();
      return;
    }

    const layer = mapView?.map?.findLayerById(
      focSeEvent
        ? USCG_FOC_SE_ASSETS_LAYER_ID
        : facilityPack
          ? facilityPack.assetsLayerId
          : CFA_HELENE_STORES_LAYER_ID
    ) as GraphicsLayer | undefined;
    const liveGraphic = layer?.graphics.toArray().find((graphic) => {
      const attrs = graphic.attributes ?? {};
      if (storeId && String(attrs.id ?? '') === storeId) return true;
      if (
        store?.storeNumber &&
        String(attrs.storeNumber ?? '') === store.storeNumber
      ) {
        return true;
      }
      return false;
    });

    if (liveGraphic) {
      applyHeleneResourcePopupToGraphic({
        graphic: liveGraphic,
        resource,
        store,
        aorNames,
      });
      void focusFeatureOnMap({ graphic: liveGraphic });
      return;
    }

    focusFromResourceGeometry();
  };

  return (
    <div className="tw:min-w-0 tw:space-y-4 tw:text-sm tw:leading-snug">
      {includeAssets ? (
        <div className="tw:min-w-0">
          <p className="tw:m-0 tw:font-medium tw:text-foreground">
            Assets at Risk
            {orgEvent?.region
              ? ` in ${orgEvent.region}`
              : cfaEnabled || focSeEvent
                ? ` in ${regionalThreats.region}`
                : ''}
          </p>
          {cfaEnabled || coneResources.length > 0 ? (
            <div className="tw:mt-2 tw:min-w-0 tw:space-y-1.5">
              {demoKind === 'epa' && coneResources.length > 0 ? (
                <Input
                  value={assetSearch}
                  onChange={(event) => setAssetSearch(event.target.value)}
                  placeholder="Search assets at risk"
                  aria-label="Search assets at risk"
                  className="tw:h-8"
                />
              ) : null}
              {coneResources.length === 0 ? (
                <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                  {cfaEnabled
                    ? facilityPack
                      ? `Seeding ${facilityPack.label} resources…`
                      : demoKind === 'epa'
                        ? 'Seeding EPA resources…'
                        : 'Seeding CFA resources…'
                    : 'No assets at risk.'}
                </p>
              ) : filteredConeResources.length === 0 ? (
                <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                  No matching assets.
                </p>
              ) : (
                visibleConeResources.map((resource) => (
                  <CfaHeleneAssetItem
                    key={resource.resource_set_id}
                    resource={resource}
                    orgEvent={orgEvent}
                    eventName={eventName}
                    row={resourceSetToRow(
                      resource,
                      groupMembers,
                      cachedNplSites,
                      automationRules
                    )}
                    density={assetItemDensity}
                    selected={
                      selectedResourceSetId === resource.resource_set_id
                    }
                    open={openAssetIds.has(resource.resource_set_id)}
                    onOpenChange={(nextOpen) => {
                      setOpenAssetIds((previous) => {
                        const next = new Set(previous);
                        if (nextOpen) next.add(resource.resource_set_id);
                        else next.delete(resource.resource_set_id);
                        return next;
                      });
                    }}
                    onMapClick={() => {
                      setSelectedResourceSetId(resource.resource_set_id);
                      setOpenAssetIds((previous) => {
                        const next = new Set(previous);
                        next.add(resource.resource_set_id);
                        return next;
                      });
                      focusHeleneStoreOnMap(resource);
                    }}
                    persistedChecklistItems={resourceChecklistItems(
                      persistedChecklistItems,
                      resource.resource_set_id
                    )}
                    usePersistedChecklists={eventHasPersistedResourceChecklists}
                    mapSymbolVisible={
                      !hiddenSymbolIds.has(resource.resource_set_id)
                    }
                    onMapSymbolVisibleChange={(visible) =>
                      setAssetSymbolVisible(resource.resource_set_id, visible)
                    }
                    onChecklistNotifySent={(itemId, notificationId) => {
                      const stored =
                        localChecklistItems ?? responseChecklistItems ?? [];
                      const next = stored.map((row) =>
                        row.kind === 'send_notification' && row.id === itemId
                          ? { ...row, sentNotificationId: notificationId }
                          : row
                      );
                      setLocalChecklistItems(next);
                      if (persistableEventId) {
                        void updateOrgEvent.mutateAsync({
                          id: persistableEventId,
                          response_checklist_items: next,
                        });
                      }
                    }}
                    onEditClick={() => {
                      const storeId = facilityPack
                        ? facilityPack.facilityIdFromResourceNotes(
                            resource.notes
                          )
                        : demoKind === 'epa'
                          ? epaFacilityIdFromResourceNotes(resource.notes)
                          : heleneStoreIdFromResourceNotes(resource.notes);
                      const store = facilityPack
                        ? findAnyFacilityDemoSite(storeId)
                        : demoKind === 'epa'
                          ? findEpaGulfSite(storeId, cachedNplSites)
                          : storeId
                            ? CFA_HELENE_STORES.find(
                                (entry) => entry.id === storeId
                              )
                            : undefined;
                      const hasGeometry =
                        resource.geometry &&
                        typeof resource.geometry === 'object' &&
                        Array.isArray(resource.geometry.coordinates);
                      if (!hasGeometry && store) {
                        setSelectedResource({
                          ...resource,
                          geometry: {
                            type: 'Point',
                            coordinates: [store.lon, store.lat],
                          },
                        });
                        return;
                      }
                      setSelectedResource(resource);
                    }}
                  />
                ))
              )}
              {demoKind === 'epa' &&
              filteredConeResources.length > EPA_ASSET_PAGE_SIZE ? (
                <Pagination className="tw:pt-1">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href={void 0}
                        onClick={(event) => {
                          event.preventDefault();
                          setAssetPage((page) => Math.max(1, page - 1));
                        }}
                        aria-disabled={assetPage <= 1}
                        className={
                          assetPage <= 1
                            ? 'tw:pointer-events-none tw:opacity-50'
                            : undefined
                        }
                      />
                    </PaginationItem>
                    <PaginationItem>
                      <span className="tw:px-2 tw:text-xs tw:text-muted-foreground">
                        {assetPage} / {assetPageCount}
                      </span>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext
                        href={void 0}
                        onClick={(event) => {
                          event.preventDefault();
                          setAssetPage((page) =>
                            Math.min(assetPageCount, page + 1)
                          );
                        }}
                        aria-disabled={assetPage >= assetPageCount}
                        className={
                          assetPage >= assetPageCount
                            ? 'tw:pointer-events-none tw:opacity-50'
                            : undefined
                        }
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <TriggeringMapItems items={orgEvent?.triggering_items} />

      <div>
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
          <p className="tw:m-0 tw:font-medium tw:text-foreground">
            {COURSES_OF_ACTION_LABEL}
          </p>
          <LaunchScenarioButton orgEventId={persistableEventId} size="xs" />
        </div>
        <div className="tw:mt-2 tw:space-y-1.5">
          {mixedChecklist.length === 0 ? (
            <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
              {(cfaEnabled || focSeEvent) &&
              persistedChecklistItems.length === 0
                ? 'Seeding org actions…'
                : 'No response plan configured.'}
            </p>
          ) : (
            mixedChecklist.map((entry, index) => {
              if (entry.kind === 'send_notification') {
                return (
                  <Item
                    key={`notify-${entry.id}-${index}`}
                    variant="outline"
                    size="xs"
                    className="tw:cursor-pointer tw:items-start tw:gap-2 tw:px-2.5 tw:py-2 tw:hover:bg-muted/40"
                    onClick={() => setNotifyItem(entry)}
                  >
                    <ItemContent className="tw:min-w-0">
                      <ItemTitle className="tw:line-clamp-3 tw:text-sm tw:font-normal tw:text-foreground">
                        {entry.label}
                      </ItemTitle>
                      <p className="tw:m-0 tw:mt-0.5 tw:text-[11px] tw:text-muted-foreground">
                        Send Notification
                      </p>
                    </ItemContent>
                    <ItemActions>
                      <Badge className="tw:shrink-0 tw:text-[10px]">
                        {entry.sentNotificationId ? 'Sent' : 'Compose'}
                      </Badge>
                    </ItemActions>
                  </Item>
                );
              }

              const action = actionById.get(String(entry.actionId));
              const label = action?.name || entry.label || 'Org Action';
              return (
                <Item
                  key={`action-${entry.actionId}-${index}`}
                  variant="outline"
                  size="xs"
                  className="tw:cursor-pointer tw:items-start tw:gap-2 tw:px-2.5 tw:py-2 tw:hover:bg-muted/40"
                  onClick={() => {
                    if (action) setSelectedAction(action);
                  }}
                >
                  <ItemContent className="tw:min-w-0">
                    <ItemTitle className="tw:line-clamp-3 tw:text-sm tw:font-normal tw:text-foreground">
                      {label}
                    </ItemTitle>
                    <p className="tw:m-0 tw:mt-0.5 tw:text-[11px] tw:text-muted-foreground">
                      {facilityPack
                        ? formatAssignedActionPoc({
                            poc: action?.poc,
                            pocUserName: action?.poc_user_name,
                            roster: groupMembers,
                          })
                        : action?.poc_user_name || 'Unassigned'}
                    </p>
                  </ItemContent>
                  <ItemActions>
                    <Badge
                      className={cn(
                        'tw:shrink-0 tw:text-[10px]',
                        actionStatusBadgeClass(action?.status)
                      )}
                    >
                      {action?.status || 'Planned'}
                    </Badge>
                  </ItemActions>
                </Item>
              );
            })
          )}
        </div>
      </div>

      {selectedAction ? (
        <ActionDrawer
          scope={ORGANIZATION}
          selectedEntity={selectedAction}
          toggle={() => setSelectedAction(null)}
        />
      ) : null}
      {selectedResource ? (
        <ResourceSetEditDrawer
          selectedEntity={selectedResource}
          toggle={() => setSelectedResource(null)}
        />
      ) : null}
      <ChecklistNotifyComposeModal
        open={!!notifyItem}
        item={notifyItem}
        eventName={eventName}
        orgEvent={orgEvent}
        onOpenChange={(open) => {
          if (!open) setNotifyItem(null);
        }}
        onSent={(notificationId) => {
          if (!notifyItem) return;
          const stored = localChecklistItems ?? responseChecklistItems ?? [];
          const base = stored.length > 0 ? stored : mixedChecklist;
          const next = base.map((row) =>
            row.kind === 'send_notification' && row.id === notifyItem.id
              ? { ...row, sentNotificationId: notificationId }
              : row
          );
          setLocalChecklistItems(next);
          if (persistableEventId) {
            void updateOrgEvent.mutateAsync({
              id: persistableEventId,
              response_checklist_items: next,
            });
          }
          setNotifyItem(null);
        }}
      />
    </div>
  );
}
